import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

export default function Contact() {
  const CONTACT_CLIENT_KEY = "rl_contact_client_id_v1";
  const CONTACT_NAME_KEY = "rl_contact_name_v1";

  const [clientId, setClientId] = React.useState("");
  const [name, setName] = React.useState("");
  const [nicknameDraft, setNicknameDraft] = React.useState("");
  const [threadId, setThreadId] = React.useState("");
  const [draft, setDraft] = React.useState("");
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");

  const ensureClientId = React.useCallback(() => {
    try {
      let id = localStorage.getItem(CONTACT_CLIENT_KEY) || "";
      if (!id) {
        id = typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID().replace(/-/g, "")
          : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem(CONTACT_CLIENT_KEY, id);
      }
      setClientId(id);
      return id;
    } catch (_) {
      const fallback = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      setClientId(fallback);
      return fallback;
    }
  }, []);

  const hasNickname = (name || "").trim().length >= 2;

  const fetchConversationState = React.useCallback(async (idArg) => {
    const id = idArg || clientId;
    if (!id) return;
    setLoading(true);
    try {
      const { getContactConversations, getContactConversationMessages } = await import("../lib/api");
      const items = await getContactConversations(id);
      const firstThread = Array.isArray(items) ? items[0] : null;
      const nextThreadId = firstThread?.id || "";
      setThreadId(nextThreadId);

      if (!nextThreadId) {
        setMessages([]);
      } else {
        setLoadingMessages(true);
        const list = await getContactConversationMessages(nextThreadId, id);
        setMessages(Array.isArray(list) ? list : []);
      }
      setError("");
    } catch (_) {
      setError("Could not load messages right now.");
      setMessages([]);
    } finally {
      setLoading(false);
      setLoadingMessages(false);
    }
  }, [clientId]);

  React.useEffect(() => {
    const id = ensureClientId();
    try {
      const savedName = localStorage.getItem(CONTACT_NAME_KEY) || "";
      setName(savedName);
      setNicknameDraft(savedName);
    } catch (_) {}
    fetchConversationState(id);
  }, [ensureClientId, fetchConversationState]);

  React.useEffect(() => {
    if (!clientId || !hasNickname) return undefined;
    const timer = window.setInterval(() => {
      fetchConversationState(clientId);
    }, 9000);
    return () => window.clearInterval(timer);
  }, [clientId, hasNickname, fetchConversationState]);

  const saveNickname = (e) => {
    e.preventDefault();
    const clean = (nicknameDraft || "").trim();
    if (clean.length < 2 || clean.length > 40) {
      setError("Nickname must be 2 to 40 characters.");
      return;
    }
    setName(clean);
    try { localStorage.setItem(CONTACT_NAME_KEY, clean); } catch (_) {}
    setError("");
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    const cleanName = (name || "").trim();
    const cleanDraft = (draft || "").trim();
    if (!cleanName || !cleanDraft) {
      setError("Please add your name and message.");
      return;
    }

    setSending(true);
    setError("");
    try {
      localStorage.setItem(CONTACT_NAME_KEY, cleanName);
      const { sendContactConversationMessage } = await import("../lib/api");
      const data = await sendContactConversationMessage({
        clientId,
        name: cleanName,
        message: cleanDraft,
        threadId,
      });

      const nextThreadId = data?.thread?.id || threadId;
      if (nextThreadId) setThreadId(nextThreadId);
      setDraft("");
      await fetchConversationState(clientId);
      if (nextThreadId) {
        const { getContactConversationMessages } = await import("../lib/api");
        const list = await getContactConversationMessages(nextThreadId, clientId);
        setMessages(Array.isArray(list) ? list : []);
      }
    } catch (_) {
      setError("Sorry, we couldn't send your message right now.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10" data-testid="contact-chat-page">
      <Card className="elevated-card border border-emerald-500/30 shadow-[0_10px_30px_rgba(16,185,129,0.12)]">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100" data-testid="contact-chat-title">Contact Support</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-slate-700 dark:text-slate-300">
          <p className="text-sm text-slate-500" data-testid="contact-chat-subtitle">
            Send a message directly. We keep one conversation per nickname on this device.
          </p>

          {error && <div className="text-sm text-red-600" data-testid="contact-chat-error">{error}</div>}

          {!hasNickname && (
            <form onSubmit={saveNickname} className="rounded-xl border border-emerald-300/50 bg-emerald-50/50 p-4" data-testid="contact-nickname-prompt">
              <div className="text-sm font-semibold text-slate-900" data-testid="contact-nickname-prompt-title">Choose your nickname first</div>
              <div className="text-xs text-slate-600 mt-1" data-testid="contact-nickname-prompt-subtitle">This nickname will identify your messages to admin.</div>
              <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
                <input
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  placeholder="Enter nickname"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  data-testid="contact-nickname-input"
                />
                <Button type="submit" data-testid="contact-nickname-save-button">Continue</Button>
              </div>
            </form>
          )}

          <div className="border rounded-xl p-3 h-[56vh] flex flex-col bg-slate-950/95" data-testid="contact-message-panel">
            <div className="border-b border-emerald-500/30 pb-3 mb-3 flex items-center justify-between" data-testid="contact-panel-header">
              <div>
                <div className="text-xs text-emerald-300">You are chatting as</div>
                <div className="text-sm font-semibold text-emerald-100" data-testid="contact-active-nickname">{hasNickname ? name : "(set nickname first)"}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setName("");
                  setNicknameDraft("");
                  try { localStorage.removeItem(CONTACT_NAME_KEY); } catch (_) {}
                }}
                className="text-xs px-2 py-1 rounded border border-emerald-400/40 text-emerald-200"
                data-testid="contact-change-nickname-button"
              >
                Change
              </button>
            </div>

            <div className="flex-1 overflow-auto space-y-3" data-testid="contact-message-list">
              {(loading || loadingMessages) && <div className="text-sm text-slate-400" data-testid="contact-messages-loading">Loading…</div>}
              {!loadingMessages && messages.length === 0 && <div className="text-sm text-slate-400" data-testid="contact-empty-state">No messages yet. Send one to start.</div>}
              {messages.map((msg) => (
                <div key={msg.id} className="flex justify-end" data-testid={`contact-message-${msg.id}`}>
                  <div className="max-w-[82%] rounded-2xl bg-emerald-500/85 text-slate-950 px-3 py-2 shadow">
                    <div className="text-xs opacity-85">{msg.name}</div>
                    <div className="text-sm whitespace-pre-wrap">{msg.message}</div>
                    <div className="mt-1 text-[11px] opacity-75">{new Date(msg.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={submitMessage} className="mt-3 pt-3 border-t border-emerald-500/30" data-testid="contact-compose-form">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full min-h-[90px] rounded-md border border-emerald-300/40 bg-slate-900 text-slate-100 px-3 py-2 text-sm"
                placeholder={hasNickname ? "Write your message..." : "Set nickname to enable messaging"}
                data-testid="contact-message-input"
                disabled={!hasNickname}
              />
              <div className="mt-2 flex justify-end">
                <Button type="submit" className="text-sm" variant="default" disabled={sending || !hasNickname} data-testid="contact-send-button">
                  {sending ? "Sending..." : "Send"}
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}